import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import SendRounded from '@mui/icons-material/SendRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import LocationOnRounded from '@mui/icons-material/LocationOnRounded';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import AppAppBar from '../components/AppAppBar';
import Footer from '../components/Footer';
import { Section, Eyebrow } from '../components/Section';
import Reveal from '../components/Reveal';
import { Seo } from '../components/Seo';
import { routeUrl } from '../seo/routes';
import { azure, emerald, glass, line, lineStrong, slate } from '../theme';

/**
 * /contact: a form that emails the team, replacing the Microsoft Bookings
 * drawer that used to open from every "Book a consultation" button.
 *
 * The browser only ever talks to /api/contact.php on this same host, which
 * holds the Resend key and does the flood protection (public/api/contact.php
 * explains the layers). The checks here are for the visitor's benefit, not
 * security: the server repeats every one of them.
 *
 * Two of those layers live partly on this side. The "website" field is a
 * honeypot, positioned off screen and hidden from assistive technology, so
 * only a bot fills it in. And `elapsed` reports how long the form was open:
 * anything under three seconds is a script, and the server quietly drops it.
 */

const ENDPOINT = `${process.env.PUBLIC_URL || ''}/api/contact.php`;

// The same keys as TOPICS in contact.php. Panels and pages link here with
// ?topic=<key> so the visitor arrives with the subject already chosen.
export const TOPICS = ['general', 'power', 'apps', 'cyber', 'nizro', 'fawtara'];

const MAX = { name: 100, email: 254, company: 120, message: 5000 };
const MESSAGE_MIN = 10;
const EMPTY = { name: '', email: '', company: '', topic: 'general', message: '', website: '' };

function validate(v) {
  const errors = {};
  if (!v.name.trim()) errors.name = ['required'];
  else if (v.name.trim().length > MAX.name) errors.name = ['tooLong', { max: MAX.name }];

  const email = v.email.trim();
  if (!email) errors.email = ['required'];
  else if (email.length > MAX.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = ['email'];

  if (v.company.trim().length > MAX.company) errors.company = ['tooLong', { max: MAX.company }];

  const message = v.message.trim();
  if (!message) errors.message = ['required'];
  else if (message.length < MESSAGE_MIN) errors.message = ['tooShort', { min: MESSAGE_MIN }];
  else if (message.length > MAX.message) errors.message = ['tooLong', { max: MAX.message }];

  return errors;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: alpha('#FFFFFF', 0.025),
    '& fieldset': { borderColor: lineStrong },
    '&:hover fieldset': { borderColor: alpha(slate[300], 0.5) },
    '&.Mui-focused fieldset': { borderColor: azure[400] },
  },
};

function topicLabel(t, key) {
  if (key === 'general') return t('contactPage.topicGeneral');
  if (key === 'nizro' || key === 'fawtara') return t(`nav.${key}`);
  return t(`services.${key}.title`);
}

function ContactForm() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const requested = params.get('topic');
  const [values, setValues] = React.useState(() => ({
    ...EMPTY,
    topic: TOPICS.includes(requested) ? requested : 'general',
  }));
  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState('idle'); // idle | sending | sent | error
  const [failure, setFailure] = React.useState('generic');
  const [sentTo, setSentTo] = React.useState(null);
  const openedAt = React.useRef(Date.now());
  const formRef = React.useRef(null);

  const update = (key) => (event) => {
    const value = event.target.value;
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors(({ [key]: _gone, ...rest }) => rest);
  };

  const helper = (key) => (errors[key] ? t(`contactPage.errors.${errors[key][0]}`, errors[key][1]) : ' ');

  const focusFirstInvalid = (errs) => {
    const first = ['name', 'email', 'company', 'message'].find((k) => errs[k]);
    if (first) formRef.current?.querySelector(`[name="${first}"]`)?.focus();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (status === 'sending') return;
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(errs);
      return;
    }

    setStatus('sending');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          company: values.company.trim(),
          topic: values.topic,
          message: values.message.trim(),
          website: values.website,
          elapsed: Date.now() - openedAt.current,
          lang: i18n.language === 'ar' ? 'ar' : 'en',
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setSentTo({ name: values.name.trim(), email: values.email.trim() });
        setStatus('sent');
        return;
      }
      if (res.status === 422 && Array.isArray(data.fields)) {
        const serverErrs = Object.fromEntries(data.fields.map((f) => [f, ['invalid']]));
        setErrors(serverErrs);
        setStatus('idle');
        focusFirstInvalid(serverErrs);
        return;
      }
      setFailure(['links', 'rate', 'busy'].includes(data.error) ? data.error : 'generic');
      setStatus('error');
    } catch {
      setFailure('generic');
      setStatus('error');
    }
  };

  const reset = () => {
    setValues((v) => ({ ...EMPTY, topic: v.topic }));
    setErrors({});
    setStatus('idle');
    openedAt.current = Date.now();
  };

  if (status === 'sent' && sentTo) {
    return (
      <Box role="status" sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
        <CheckCircleRounded sx={{ fontSize: 52, color: emerald[400] }} />
        <Typography variant="h4" component="h2" sx={{ mt: 2 }}>
          {t('contactPage.sentTitle')}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 420, mx: 'auto' }}>
          {t('contactPage.sentBody', sentTo)}
        </Typography>
        <Button variant="text" onClick={reset} sx={{ mt: 3, color: azure[300] }}>
          {t('contactPage.sendAnother')}
        </Button>
      </Box>
    );
  }

  const sending = status === 'sending';

  return (
    <Box component="form" ref={formRef} noValidate onSubmit={submit} aria-labelledby="contact-form-title">
      <Typography id="contact-form-title" variant="h5" component="h2" sx={{ mb: 3 }}>
        {t('contactPage.formTitle')}
      </Typography>

      {/* rowGap clears the next row's floating label from an error line above it. */}
      <Box sx={{ display: 'grid', columnGap: 2, rowGap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          name="name"
          label={t('contactPage.name')}
          value={values.name}
          onChange={update('name')}
          error={Boolean(errors.name)}
          helperText={helper('name')}
          autoComplete="name"
          required
          fullWidth
          disabled={sending}
          inputProps={{ maxLength: MAX.name, dir: 'auto' }}
          sx={fieldSx}
        />
        <TextField
          name="email"
          type="email"
          label={t('contactPage.email')}
          value={values.email}
          onChange={update('email')}
          error={Boolean(errors.email)}
          helperText={helper('email')}
          autoComplete="email"
          required
          fullWidth
          disabled={sending}
          // Addresses are Latin and left-to-right in either language.
          inputProps={{ maxLength: MAX.email, dir: 'ltr' }}
          sx={fieldSx}
        />
        <TextField
          name="company"
          label={t('contactPage.company')}
          value={values.company}
          onChange={update('company')}
          error={Boolean(errors.company)}
          helperText={helper('company')}
          autoComplete="organization"
          fullWidth
          disabled={sending}
          inputProps={{ maxLength: MAX.company, dir: 'auto' }}
          sx={fieldSx}
        />
        <TextField
          name="topic"
          select
          label={t('contactPage.topic')}
          value={values.topic}
          onChange={update('topic')}
          helperText=" "
          fullWidth
          disabled={sending}
          sx={fieldSx}
        >
          {TOPICS.map((key) => (
            <MenuItem key={key} value={key}>
              {topicLabel(t, key)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          name="message"
          label={t('contactPage.message')}
          value={values.message}
          onChange={update('message')}
          error={Boolean(errors.message)}
          helperText={helper('message')}
          required
          fullWidth
          multiline
          minRows={6}
          disabled={sending}
          inputProps={{ maxLength: MAX.message, dir: 'auto' }}
          sx={{ ...fieldSx, gridColumn: '1 / -1' }}
        />
      </Box>

      {/* Honeypot. Off screen rather than display:none, which some bots skip. */}
      <Box aria-hidden="true" sx={{ position: 'absolute', insetInlineStart: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          {t('contactPage.honeypot')}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={values.website}
            onChange={update('website')}
          />
        </label>
      </Box>

      {status === 'error' && (
        <Alert severity="error" variant="outlined" sx={{ mt: 1, mb: 2.5, borderRadius: '12px' }}>
          {t(`contactPage.fail.${failure}`)}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mt: 1 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
          {t('contactPage.privacy')}
        </Typography>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={sending}
          startIcon={
            sending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendRounded sx={{ transform: i18n.dir() === 'rtl' ? 'scaleX(-1)' : 'none' }} />
            )
          }
          sx={{ flex: 'none' }}
        >
          {sending ? t('contactPage.sending') : t('contactPage.send')}
        </Button>
      </Stack>
    </Box>
  );
}

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';

  // The router keeps the scroll position across navigation, so arriving from
  // a button low on the home page would otherwise open this page at its foot.
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      <Seo
        page="contactPage"
        path="/contact"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: t('contactPage.metaTitle'),
          url: routeUrl(lang, '/contact'),
          inLanguage: lang,
          about: { '@id': 'https://www.dynapage.co.uk/#organization' },
        }}
      />
      <AppAppBar />

      <Section id="contact-page" sx={{ pt: { xs: 14, md: 18 } }}>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 6, md: 8 },
            gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' },
            alignItems: 'start',
          }}
        >
          <Reveal>
            <Eyebrow>{t('contact.eyebrow')}</Eyebrow>
            <Typography variant="h1" sx={{ fontSize: 'clamp(2rem, 3.6vw, 3rem)' }}>
              {t('contact.title')}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 2.5, color: 'text.secondary', maxWidth: 520 }}>
              {t('contact.lead')}
            </Typography>

            <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mt: 5, pt: 3.5, borderTop: `1px solid ${line}` }}>
              <LocationOnRounded sx={{ color: azure[300], fontSize: 20, mt: '3px' }} />
              <Box>
                <Typography variant="overline" component="p" sx={{ color: 'text.secondary' }}>
                  {t('contact.visit')}
                </Typography>
                <Typography variant="body2" sx={{ color: slate[300] }}>
                  {t('contact.address')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {t('footer.reg')}
                </Typography>
              </Box>
            </Stack>
          </Reveal>

          <Box
            sx={{
              position: 'relative',
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
              border: `1px solid ${alpha(azure[300], 0.22)}`,
              bgcolor: glass,
              boxShadow: `0 30px 80px ${alpha('#000000', 0.35)}`,
            }}
          >
            <ContactForm />
          </Box>
        </Box>
      </Section>

      <Footer />
    </Box>
  );
}
